import { FlightService, HotelService, ServiceBase } from "@/models/orders";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import { formatDateTime, formatAmount } from "@/util";
import { ServiceType } from "@/models/enums";

interface ServiceDetailsProps {
    service: ServiceBase;
}

const ServiceDetails = ({ service }: ServiceDetailsProps) => {
    const { t } = useTranslation();
    const [showExtraInfo, setShowExtraInfo] = useState(false);

    const serviceExtraInfo = () => {
        if (!showExtraInfo) return;

        let serviceInfo;
        switch (service.serviceType.toString()) {
            case ServiceType[ServiceType.HotelService]: {
                serviceInfo = hotelServiceExtraInfo(service as HotelService);
                break;
            }
            case ServiceType[ServiceType.FlightService]: {
                serviceInfo = flightServiceExtraInfo(service as FlightService);
                break;
            }
            default: {
                serviceInfo = "";
            }
        }

        return (
            <>
                <tr className="service-info-row">
                    <td colSpan={4}>
                        <div className="service-info-capacity-row">
                            <div className="service-info-capacity">
                                <div>
                                    <div className="title">{t("service.numberOfAdults")}:</div>
                                    <div className="value">&nbsp;{service.numberAdults}</div>
                                </div>
                                <div>
                                    <div className="title">{t("service.numberOfChilds")}:</div>
                                    <div className="value">&nbsp;{service.numberChilds}</div>
                                </div>
                                <div>
                                    <div className="title">{t("service.numberOfInfants")}:</div>
                                    <div className="value">&nbsp;{service.numberInfants}</div>
                                </div>
                            </div>
                            <div></div>
                        </div>
                    </td>
                    <td></td>
                </tr>
                <tr>
                    <td colSpan={5}>
                        <div className="divider"></div>
                    </td>
                </tr>
                {serviceInfo}
            </>
        );
    };

    const hotelServiceExtraInfo = (hotelService: HotelService) => (
        <>
            <tr className="service-info-row">
                <td style={{ whiteSpace: "nowrap" }} className="title">
                    {t("service.hotelName")}:
                </td>
                <td className="value">{hotelService.hotelName}</td>
                <td style={{ whiteSpace: "nowrap" }} className="title">
                    {t("service.grossPrice")}:
                </td>
                <td className="value">{formatAmount(hotelService.grossPrice, hotelService.currencyId)}</td>
                <td></td>
            </tr>
            <tr className="service-info-row">
                <td style={{ whiteSpace: "nowrap" }} className="title">
                    {t("service.region")}:
                </td>
                <td className="value">{hotelService.regionName}</td>
                <td style={{ whiteSpace: "nowrap" }} className="title">
                    {t("service.totalServiceAmount")}:
                </td>
                <td className="value">{formatAmount(hotelService.totalPrice, hotelService.currencyId)}</td>
                <td></td>
                <td></td>
            </tr>
            <tr className="service-info-row">
                <td style={{ whiteSpace: "nowrap" }} className="title">
                    {t("service.roomTypeName")}:
                </td>
                <td className="value">{hotelService.roomTypeName}</td>
                <td></td>
                <td></td>
                <td></td>
            </tr>
            <tr className="service-info-row">
                <td style={{ whiteSpace: "nowrap" }} className="title">
                    {t("service.boardBaseName")}:
                </td>
                <td className="value">{hotelService.boardBaseName}</td>
                <td></td>
                <td></td>
                <td width={120}>
                    {" "}
                    {showExtraInfo && (
                        <Button
                            variant="contained"
                            className="service-show-more-button"
                            onClick={() => setShowExtraInfo(!showExtraInfo)}
                            endIcon={
                                <KeyboardDoubleArrowUpIcon
                                    sx={{
                                        fontSize: "16px",
                                    }}
                                />
                            }
                        >
                            {t("service.button.showLess")}
                        </Button>
                    )}
                </td>
            </tr>
        </>
    );

    const flightServiceExtraInfo = (flightService: FlightService) => (
        <div className="service-info-row">
            <div className="service-info-col">
                <div className="title">{t("service.Name")}:</div>
                <div className="value">{flightService.name}</div>
            </div>
            <div className="service-info-col">
                <div className="title"></div>
                <div className="value"></div>
            </div>
            <div>
                {" "}
                {showExtraInfo && (
                    <Button
                        variant="contained"
                        className="service-show-more-button"
                        onClick={() => setShowExtraInfo(!showExtraInfo)}
                        endIcon={
                            <KeyboardDoubleArrowUpIcon
                                sx={{
                                    fontSize: "16px",
                                }}
                            />
                        }
                    >
                        {t("service.button.showLess")}
                    </Button>
                )}
            </div>
        </div>
    );

    return (
        <div className="service-container" key={service.serviceBaseId}>
            <div className="service-header">
                {service.serviceNumber} {t(`enums.ServiceType.${service.serviceType}`)}
            </div>
            <div className="service-content">
                <table className="service-info-table">
                    <tbody>
                        <tr className="service-info-row">
                            <td style={{ width: 100, whiteSpace: "nowrap" }} className="title">
                                {t("service.serviceType")}:
                            </td>
                            <td className="value">{t(`enums.ServiceType.${service.serviceType}`)}</td>
                            <td style={{ width: 100, whiteSpace: "nowrap" }} className="title">
                                {t("service.startDate")}:
                            </td>
                            <td className="value">{formatDateTime(service.fromDate)}</td>
                            <td></td>
                        </tr>
                        <tr className="service-info-row">
                            <td className="title">{t("service.providerName")}:</td>
                            <td className="value">{service.providerName}</td>
                            <td className="title">{t("service.endDate")}:</td>
                            <td className="value">{formatDateTime(service.toDate)}</td>
                            <td width={120}>
                                {" "}
                                {!showExtraInfo && (
                                    <Button
                                        variant="contained"
                                        className="service-show-more-button"
                                        onClick={() => setShowExtraInfo(!showExtraInfo)}
                                        endIcon={
                                            <KeyboardDoubleArrowDownIcon
                                                sx={{
                                                    fontSize: "16px",
                                                }}
                                            />
                                        }
                                    >
                                        {t("service.button.showMore")}
                                    </Button>
                                )}
                            </td>
                        </tr>
                        {serviceExtraInfo()}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ServiceDetails;
